import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ArrowLeft, Search, ChevronRight, ChevronDown, Plus, Minus, X, Package, FileText, LayoutList, Users, Globe, ExternalLink, Check } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useMenuItems, type MenuItemRow } from "@/hooks/useMenuItems";
import { useCategories, type Category } from "@/hooks/useCategories";
import { usePricingTypes, type PricingType } from "@/hooks/usePricingTypes";
import { usePackages, useEventTypes, useCreateEventType, useCreatePackage, useUpdatePackage, type CreatePackageInput } from "@/hooks/usePackages";
import { toast } from "sonner";
import { captureError, getErrorMessage } from "@/lib/error-message";
import { uploadWebsiteAsset } from "@/lib/storage";
import { Upload, X as XIcon } from "lucide-react";

interface SelectedItem {
  menu_item_id: string;
  quantity: number;
  notes: string;
}

interface SlotEntry {
  category_id: string;
  quantity: number;
  sort_order: number;
  label_override: string;
  excluded_item_ids: string[];
}

type Setter<T> = (value: T) => void;

const PackageEditorPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const { data: packages } = usePackages();
  const { data: menuItems } = useMenuItems();
  const { data: categories } = useCategories();
  const { data: eventTypes } = useEventTypes();
  const { data: pricingTypes } = usePricingTypes();
  const createPkg = useCreatePackage();
  const updatePkg = useUpdatePackage();
  const createET = useCreateEventType();

  const editingPackage = useMemo(() => packages?.find((p) => p.id === id) ?? null, [packages, id]);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [pricingType, setPricingType] = useState("per_person");
  const [price, setPrice] = useState(0);
  const [minGuests, setMinGuests] = useState("");
  const [maxGuests, setMaxGuests] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [selectionMode, setSelectionMode] = useState<"fixed" | "customer_choice">("fixed");
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  const [categorySlots, setCategorySlots] = useState<SlotEntry[]>([]);
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([]);
  const [itemSearch, setItemSearch] = useState("");
  const [newEventType, setNewEventType] = useState("");
  const [etPopoverOpen, setEtPopoverOpen] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Publishing fields
  const [slug, setSlug] = useState("");
  const [isPublished, setIsPublished] = useState(false);
  const [coverImageUrl, setCoverImageUrl] = useState("");
  const [ctaText, setCtaText] = useState("Request a Quote");
  const [showPrices, setShowPrices] = useState(true);

  useEffect(() => {
    if (isEditing && editingPackage && !initialized) {
      setName(editingPackage.name);
      setDescription(editingPackage.description || "");
      setPricingType(editingPackage.pricing_type);
      setPrice(Number(editingPackage.price));
      setMinGuests(editingPackage.min_guests?.toString() || "");
      setMaxGuests(editingPackage.max_guests?.toString() || "");
      setIsActive(editingPackage.is_active);
      setSelectionMode((editingPackage.selection_mode as "fixed" | "customer_choice") || "fixed");
      setSelectedItems(
        editingPackage.items?.map((i) => ({ menu_item_id: i.menu_item_id, quantity: i.quantity, notes: i.notes || "" })) || []
      );
      setCategorySlots(
        editingPackage.category_slots
          ?.sort((a, b) => a.sort_order - b.sort_order)
          .map((s) => ({ category_id: s.category_id, quantity: s.quantity, sort_order: s.sort_order, label_override: s.label_override || "", excluded_item_ids: (s.exclusions || []).map((ex) => ex.menu_item_id) })) || []
      );
      setSelectedEventTypes(editingPackage.event_types?.map((et) => et.event_type_id) || []);
      setSlug(editingPackage.slug || "");
      setIsPublished(editingPackage.is_published || false);
      setCoverImageUrl(editingPackage.cover_image_url || "");
      setCtaText(editingPackage.cta_text || "Request a Quote");
      setShowPrices(editingPackage.show_prices !== false);
      setInitialized(true);
    }
    if (!isEditing && !initialized) setInitialized(true);
  }, [isEditing, editingPackage, initialized]);

  // Track dirty state
  useEffect(() => {
    if (initialized) setDirty(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `initialized` is read, not watched: flipping it must not mark the form dirty
  }, [name, description, pricingType, price, minGuests, maxGuests, isActive, selectionMode, selectedItems, categorySlots, selectedEventTypes, slug, isPublished, coverImageUrl, ctaText, showPrices]);

  // Warn before navigating away with unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirty) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const activeCategories = useMemo(() => categories?.filter((c) => c.is_active) || [], [categories]);

  const groupedItems = useMemo(() => {
    if (!menuItems || !categories) return [];
    const search = itemSearch.toLowerCase();
    const filtered = menuItems.filter(
      (mi) => mi.is_active && (mi.name.toLowerCase().includes(search) || mi.category?.name?.toLowerCase().includes(search))
    );
    return activeCategories
      .map((cat) => ({ category: cat, items: filtered.filter((mi) => mi.category_id === cat.id) }))
      .filter((g) => g.items.length > 0);
  }, [menuItems, categories, itemSearch, activeCategories]);

  const addItem = (id: string) => {
    if (!selectedItems.find((x) => x.menu_item_id === id)) {
      setSelectedItems((s) => [...s, { menu_item_id: id, quantity: 1, notes: "" }]);
    }
  };
  const removeItem = (id: string) => setSelectedItems((s) => s.filter((x) => x.menu_item_id !== id));
  const updateQuantity = (id: string, delta: number) =>
    setSelectedItems((s) => s.map((x) => (x.menu_item_id === id ? { ...x, quantity: Math.max(1, x.quantity + delta) } : x)));
  const updateNotes = (id: string, notes: string) =>
    setSelectedItems((s) => s.map((x) => (x.menu_item_id === id ? { ...x, notes } : x)));

  const addSlot = () => {
    const usedCats = new Set(categorySlots.map((s) => s.category_id));
    const available = activeCategories.find((c) => !usedCats.has(c.id));
    if (!available) { toast.error("All categories already added"); return; }
    setCategorySlots((s) => [...s, { category_id: available.id, quantity: 1, sort_order: s.length, label_override: "", excluded_item_ids: [] }]);
  };
  const removeSlot = (idx: number) => setCategorySlots((s) => s.filter((_, i) => i !== idx).map((s, i) => ({ ...s, sort_order: i })));
  const updateSlot = (idx: number, patch: Partial<SlotEntry>) =>
    setCategorySlots((s) => s.map((slot, i) => (i === idx ? { ...slot, ...patch } : slot)));

  const toggleET = (id: string) => setSelectedEventTypes((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const handleAddEventType = async () => {
    if (!newEventType.trim()) return;
    await createET.mutateAsync({ name: newEventType.trim() });
    setNewEventType("");
  };

  const isPending = createPkg.isPending || updatePkg.isPending;

  const generateSlug = (text: string) =>
    text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  // Auto-generate slug from name when creating
  useEffect(() => {
    if (!isEditing && name && !slug) {
      setSlug(generateSlug(name));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `slug` is read, not watched: clearing it by hand must not regenerate it
  }, [name, isEditing]);

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Package name is required"); return; }
    if (selectionMode === "customer_choice" && categorySlots.length === 0) {
      toast.error("Add at least one category slot"); return;
    }
    const payload: CreatePackageInput = {
      name: name.trim(),
      description: description.trim() || undefined,
      pricing_type: pricingType,
      price,
      min_guests: minGuests ? parseInt(minGuests) : undefined,
      max_guests: maxGuests ? parseInt(maxGuests) : undefined,
      is_active: isActive,
      selection_mode: selectionMode,
      item_ids: selectionMode === "fixed" ? selectedItems.map((i) => ({ menu_item_id: i.menu_item_id, quantity: i.quantity, notes: i.notes || undefined })) : [],
      event_type_ids: selectedEventTypes,
      category_slots: selectionMode === "customer_choice" ? categorySlots.map((s, i) => ({ category_id: s.category_id, quantity: s.quantity, sort_order: i, label_override: s.label_override || undefined, exclusions: s.excluded_item_ids.map((mid) => ({ menu_item_id: mid })) })) : [],
      slug: slug.trim() || undefined,
      is_published: isPublished,
      cover_image_url: coverImageUrl.trim() || null,
      cta_text: ctaText.trim() || "Request a Quote",
      show_prices: showPrices,
    };
    if (isEditing && editingPackage) {
      await updatePkg.mutateAsync({ id: editingPackage.id, ...payload });
    } else {
      await createPkg.mutateAsync(payload);
    }
    setDirty(false);
    navigate("/admin/catalog?tab=packages");
  };

  const getItemName = (menuItemId: string) => menuItems?.find((mi) => mi.id === menuItemId)?.name ?? "Unknown";
  const getItemCategory = (menuItemId: string) => menuItems?.find((m) => m.id === menuItemId)?.category?.name ?? "";
  const getCategoryName = (catId: string) => categories?.find((c) => c.id === catId)?.name ?? "Unknown";

  const totalSlotPicks = categorySlots.reduce((sum, s) => sum + s.quantity, 0);

  return (
    <DashboardLayout>
      {/* Sticky header */}
      <div className="-m-6 mb-0">
        <div className="flex items-center justify-between px-6 py-3 border-b bg-card sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/admin/catalog?tab=packages")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="font-display text-lg font-semibold text-foreground">
                {isEditing ? "Edit Package" : "New Package"}
              </h1>
              {name && <p className="text-xs text-muted-foreground">{name}</p>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 mr-2">
              <Label className="text-sm text-muted-foreground">Active</Label>
              <Switch checked={isActive} onCheckedChange={setIsActive} />
            </div>
            <Button variant="outline" onClick={() => navigate("/admin/catalog?tab=packages")}>Cancel</Button>
            <Button onClick={handleSave} disabled={isPending}>
              {isEditing ? "Save Changes" : "Create Package"}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/* Left column */}
        <div className="space-y-6">
          <PackageDetailsCard
            name={name} setName={setName}
            description={description} setDescription={setDescription}
            pricingType={pricingType} setPricingType={setPricingType}
            pricingTypes={pricingTypes}
            price={price} setPrice={setPrice}
            minGuests={minGuests} setMinGuests={setMinGuests}
            maxGuests={maxGuests} setMaxGuests={setMaxGuests}
          />
          <EventTypesCard
            eventTypes={eventTypes}
            selectedEventTypes={selectedEventTypes}
            toggleET={toggleET}
            etPopoverOpen={etPopoverOpen}
            setEtPopoverOpen={setEtPopoverOpen}
            newEventType={newEventType}
            setNewEventType={setNewEventType}
            handleAddEventType={handleAddEventType}
            createETPending={createET.isPending}
          />
          <PublishingCard
            slug={slug} setSlug={setSlug}
            isPublished={isPublished} setIsPublished={setIsPublished}
            coverImageUrl={coverImageUrl} setCoverImageUrl={setCoverImageUrl}
            ctaText={ctaText} setCtaText={setCtaText}
            showPrices={showPrices} setShowPrices={setShowPrices}
          />
          <PreviewCard
            name={name}
            description={description}
            selectionMode={selectionMode}
            selectedItems={selectedItems}
            categorySlots={categorySlots}
            getItemName={getItemName}
            getCategoryName={getCategoryName}
            price={price}
            pricingType={pricingType}
            pricingTypes={pricingTypes}
            totalSlotPicks={totalSlotPicks}
            slug={slug}
          />
        </div>

        {/* Right two columns */}
        <div className="lg:col-span-2">
          <Card className="h-full">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Package Contents</CardTitle>
                <Badge variant="secondary">
                  {selectionMode === "fixed" ? `${selectedItems.length} items` : `${totalSlotPicks} selections`}
                </Badge>
              </div>
              {/* Mode toggle */}
              <RadioGroup
                value={selectionMode}
                onValueChange={(v) => setSelectionMode(v as "fixed" | "customer_choice")}
                className="flex gap-4 mt-3"
              >
                <div className="flex items-center gap-2 cursor-pointer">
                  <RadioGroupItem value="fixed" id="mode-fixed" />
                  <Label htmlFor="mode-fixed" className="cursor-pointer text-sm flex items-center gap-1.5">
                    <LayoutList className="h-3.5 w-3.5 text-muted-foreground" />
                    Fixed Items
                  </Label>
                </div>
                <div className="flex items-center gap-2 cursor-pointer">
                  <RadioGroupItem value="customer_choice" id="mode-choice" />
                  <Label htmlFor="mode-choice" className="cursor-pointer text-sm flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5 text-muted-foreground" />
                    Customer Choice
                  </Label>
                </div>
              </RadioGroup>
              <p className="text-xs text-muted-foreground mt-1">
                {selectionMode === "fixed"
                  ? "You select the exact menu items included in this package."
                  : "You define categories and quantities — the customer picks their items."}
              </p>
            </CardHeader>

            <CardContent className="p-0">
              {selectionMode === "fixed" ? (
                <FixedItemsPicker
                  groupedItems={groupedItems}
                  selectedItems={selectedItems}
                  itemSearch={itemSearch}
                  setItemSearch={setItemSearch}
                  addItem={addItem}
                  removeItem={removeItem}
                  updateQuantity={updateQuantity}
                  updateNotes={updateNotes}
                  getItemName={getItemName}
                  getItemCategory={getItemCategory}
                />
              ) : (
                <CategorySlotBuilder
                  categorySlots={categorySlots}
                  activeCategories={activeCategories}
                  getCategoryName={getCategoryName}
                  addSlot={addSlot}
                  removeSlot={removeSlot}
                  updateSlot={updateSlot}
                  totalSlotPicks={totalSlotPicks}
                  menuItems={menuItems}
                />
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

/* ───── Sub-components ───── */

interface PackageDetailsCardProps {
  name: string; setName: Setter<string>; description: string; setDescription: Setter<string>;
  pricingType: string; setPricingType: Setter<string>; pricingTypes: PricingType[] | undefined;
  price: number; setPrice: Setter<number>; minGuests: string; setMinGuests: Setter<string>; maxGuests: string; setMaxGuests: Setter<string>;
}

function PackageDetailsCard({ name, setName, description, setDescription, pricingType, setPricingType, pricingTypes, price, setPrice, minGuests, setMinGuests, maxGuests, setMaxGuests }: PackageDetailsCardProps) {
  return (
    <Card>
      <CardHeader className="pb-4"><CardTitle className="text-base">Package Details</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Wedding Deluxe" />
        </div>
        <div className="space-y-2">
          <Label>Description</Label>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="What's included..." />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Pricing Type</Label>
            <Select value={pricingType} onValueChange={setPricingType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {pricingTypes?.map((pt) => <SelectItem key={pt.key} value={pt.key}>{pt.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Price ($)</Label>
            <Input type="number" step="0.01" min="0" value={price || ""} onChange={(e) => setPrice(parseFloat(e.target.value) || 0)} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Min Guests</Label>
            <Input type="number" value={minGuests} onChange={(e) => setMinGuests(e.target.value)} placeholder="Optional" />
          </div>
          <div className="space-y-2">
            <Label>Max Guests</Label>
            <Input type="number" value={maxGuests} onChange={(e) => setMaxGuests(e.target.value)} placeholder="Optional" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface EventTypesCardProps {
  eventTypes: { id: string; name: string }[] | undefined; selectedEventTypes: string[]; toggleET: (id: string) => void;
  etPopoverOpen: boolean; setEtPopoverOpen: Setter<boolean>; newEventType: string; setNewEventType: Setter<string>;
  handleAddEventType: () => void; createETPending: boolean;
}

function EventTypesCard({ eventTypes, selectedEventTypes, toggleET, etPopoverOpen, setEtPopoverOpen, newEventType, setNewEventType, handleAddEventType, createETPending }: EventTypesCardProps) {
  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Event Types</CardTitle>
          <Popover open={etPopoverOpen} onOpenChange={setEtPopoverOpen}>
            <PopoverTrigger asChild>
              <Button type="button" variant="ghost" size="sm" className="h-7 text-xs"><Plus className="h-3 w-3 mr-1" /> New</Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3" align="end">
              <div className="flex gap-2">
                <Input placeholder="Event type name..." value={newEventType} onChange={(e) => setNewEventType(e.target.value)} className="text-sm h-8"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddEventType(); } }} />
                <Button size="sm" className="h-8" onClick={handleAddEventType} disabled={createETPending}>Add</Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2">
          {eventTypes?.map((et) => (
            <Badge key={et.id} variant={selectedEventTypes.includes(et.id) ? "default" : "outline"} className="cursor-pointer select-none" onClick={() => toggleET(et.id)}>
              {et.name}
            </Badge>
          ))}
          {!eventTypes?.length && <p className="text-xs text-muted-foreground">No event types yet.</p>}
        </div>
      </CardContent>
    </Card>
  );
}

interface PublishingCardProps {
  slug: string; setSlug: Setter<string>; isPublished: boolean; setIsPublished: Setter<boolean>;
  coverImageUrl: string; setCoverImageUrl: Setter<string>; ctaText: string; setCtaText: Setter<string>;
  showPrices: boolean; setShowPrices: Setter<boolean>;
}

export function PublishingCard({ slug, setSlug, isPublished, setIsPublished, coverImageUrl, setCoverImageUrl, ctaText, setCtaText, showPrices, setShowPrices }: PublishingCardProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File) => {
    if (!file.type.startsWith("image/")) return;
    setUploading(true);
    try {
      const url = await uploadWebsiteAsset(file, `packages/cover-${Date.now()}-${file.name}`);
      setCoverImageUrl(url);
      toast.success("Cover image uploaded");
    } catch (err) {
      const msg = getErrorMessage(err, "Upload failed");
      captureError(err, "package_cover_upload", msg);
      toast.error(msg);
    }
    setUploading(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file?.type.startsWith("image/")) handleFile(file);
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base flex items-center gap-2"><Globe className="h-4 w-4" /> Publishing</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-sm">Published</Label>
          <Switch checked={isPublished} onCheckedChange={setIsPublished} />
        </div>
        <div className="space-y-2">
          <Label>Slug</Label>
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground">/packages/</span>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="wedding-deluxe" className="text-sm" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Cover Image</Label>
          {coverImageUrl ? (
            <div className="relative inline-block">
              <img src={coverImageUrl} alt="Cover" className="h-32 w-full object-cover rounded border border-border" />
              <button type="button" onClick={() => setCoverImageUrl("")} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-black/80">
                <XIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <div
              className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
              <p className="text-xs text-muted-foreground">{uploading ? "Uploading..." : "Drop image or click to upload"}</p>
            </div>
          )}
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} disabled={uploading} />
        </div>
        <div className="space-y-2">
          <Label>CTA Button Text</Label>
          <Input value={ctaText} onChange={(e) => setCtaText(e.target.value)} placeholder="Request a Quote" className="text-sm" />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-sm">Show Prices</Label>
          <Switch checked={showPrices} onCheckedChange={setShowPrices} />
        </div>
      </CardContent>
    </Card>
  );
}

interface PreviewCardProps {
  name: string; description: string; selectionMode: "fixed" | "customer_choice"; selectedItems: SelectedItem[]; categorySlots: SlotEntry[];
  getItemName: (menuItemId: string) => string; getCategoryName: (catId: string) => string;
  price: number; pricingType: string; pricingTypes: PricingType[] | undefined; totalSlotPicks: number; slug: string;
}

function PreviewCard({ name, description, selectionMode, selectedItems, categorySlots, getItemName, getCategoryName, price, pricingType, pricingTypes, totalSlotPicks, slug }: PreviewCardProps) {
  return (
    <Card className="bg-muted/40">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" /> Preview</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <h4 className="font-display font-semibold">{name || "Package Name"}</h4>
          {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
        </div>
        <Separator />
        {selectionMode === "fixed" ? (
          selectedItems.length > 0 ? (
            <ul className="space-y-1">
              {selectedItems.map((item) => (
                <li key={item.menu_item_id} className="text-sm flex items-center justify-between">
                  <span>{item.quantity > 1 && <span className="text-muted-foreground">{item.quantity}× </span>}{getItemName(item.menu_item_id)}</span>
                  {item.notes && <span className="text-xs text-muted-foreground italic truncate max-w-[120px]">{item.notes}</span>}
                </li>
              ))}
            </ul>
          ) : <p className="text-xs text-muted-foreground italic">No items selected yet</p>
        ) : (
          categorySlots.length > 0 ? (
            <ul className="space-y-1.5">
              {categorySlots.map((slot, i) => (
                <li key={i} className="text-sm flex items-center justify-between">
                  <span className="font-medium">{slot.label_override || getCategoryName(slot.category_id)}</span>
                  <Badge variant="secondary" className="text-xs">Select {slot.quantity}</Badge>
                </li>
              ))}
            </ul>
          ) : <p className="text-xs text-muted-foreground italic">No category slots defined yet</p>
        )}
        <Separator />
        <div className="flex justify-between text-sm font-medium">
          <span>{selectionMode === "fixed" ? `${selectedItems.length} items` : `${totalSlotPicks} selections`}</span>
          <span>${Number(price).toFixed(2)} {pricingTypes?.find((pt) => pt.key === pricingType)?.unit_label ? `/ ${pricingTypes.find((pt) => pt.key === pricingType)!.unit_label}` : ""}</span>
        </div>
        {slug && (
          <Button variant="outline" size="sm" className="w-full mt-2" asChild>
            <a href={`/packages/${slug}`} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Preview Public Page
            </a>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

interface FixedItemsPickerProps {
  groupedItems: { category: Category; items: MenuItemRow[] }[]; selectedItems: SelectedItem[]; itemSearch: string; setItemSearch: Setter<string>;
  addItem: (id: string) => void; removeItem: (id: string) => void; updateQuantity: (id: string, delta: number) => void; updateNotes: (id: string, notes: string) => void;
  getItemName: (menuItemId: string) => string; getItemCategory: (menuItemId: string) => string;
}

function FixedItemsPicker({ groupedItems, selectedItems, itemSearch, setItemSearch, addItem, removeItem, updateQuantity, updateNotes, getItemName, getItemCategory }: FixedItemsPickerProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border min-h-[500px]">
      {/* Available */}
      <div className="p-4 space-y-3 overflow-y-auto max-h-[600px]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search items..." value={itemSearch} onChange={(e) => setItemSearch(e.target.value)} className="pl-9" />
        </div>
        <div className="space-y-1">
          {groupedItems.map((group) => (
            <Collapsible key={group.category.id} defaultOpen>
              <CollapsibleTrigger className="flex items-center gap-2 w-full text-left py-2 px-2 rounded-md hover:bg-muted text-sm font-medium group">
                <ChevronRight className="h-4 w-4 transition-transform group-data-[state=open]:rotate-90 text-muted-foreground" />
                {group.category.name}
                <span className="text-muted-foreground text-xs ml-auto">{group.items.length}</span>
              </CollapsibleTrigger>
              <CollapsibleContent className="pl-4 space-y-0.5">
                {group.items.map((mi) => {
                  const isSelected = selectedItems.some((x) => x.menu_item_id === mi.id);
                  return (
                    <button key={mi.id} type="button" onClick={() => isSelected ? removeItem(mi.id) : addItem(mi.id)}
                      className={`flex items-center gap-2 w-full text-left py-2 px-3 rounded-md text-sm transition-colors ${isSelected ? "bg-primary/10 text-primary border border-primary/20" : "hover:bg-muted/60"}`}>
                      <span className="flex-1 truncate">{mi.name}</span>
                      {isSelected ? <span className="text-xs text-primary font-medium">Added</span> : <Plus className="h-3.5 w-3.5 text-muted-foreground" />}
                    </button>
                  );
                })}
              </CollapsibleContent>
            </Collapsible>
          ))}
          {groupedItems.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">{itemSearch ? "No items match your search." : "No active menu items available."}</p>}
        </div>
      </div>
      {/* Selected */}
      <div className="p-4 bg-muted/20 overflow-y-auto max-h-[600px]">
        <p className="text-sm font-medium text-muted-foreground mb-3">Selected Items</p>
        {selectedItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Package className="h-8 w-8 text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">Click items on the left to add them</p>
          </div>
        ) : (
          <div className="space-y-2">
            {selectedItems.map((item) => (
              <Card key={item.menu_item_id} className="shadow-sm">
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{getItemName(item.menu_item_id)}</p>
                      <p className="text-xs text-muted-foreground">{getItemCategory(item.menu_item_id)}</p>
                    </div>
                    <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => removeItem(item.menu_item_id)}><X className="h-3.5 w-3.5" /></Button>
                  </div>
                  <div className="flex items-center gap-1.5 bg-muted rounded-md px-1 w-fit">
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item.menu_item_id, -1)}><Minus className="h-3 w-3" /></Button>
                    <span className="text-sm w-8 text-center tabular-nums font-medium">{item.quantity}</span>
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item.menu_item_id, 1)}><Plus className="h-3 w-3" /></Button>
                  </div>
                  <Input placeholder="Add a note (optional)..." value={item.notes} onChange={(e) => updateNotes(item.menu_item_id, e.target.value)} className="h-8 text-xs" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface CategorySlotBuilderProps {
  categorySlots: SlotEntry[]; activeCategories: Category[]; getCategoryName: (catId: string) => string;
  addSlot: () => void; removeSlot: (idx: number) => void; updateSlot: (idx: number, patch: Partial<SlotEntry>) => void;
  totalSlotPicks: number; menuItems: MenuItemRow[] | undefined;
}

function CategorySlotBuilder({ categorySlots, activeCategories, getCategoryName, addSlot, removeSlot, updateSlot, totalSlotPicks, menuItems }: CategorySlotBuilderProps) {
  const usedCats = new Set(categorySlots.map((s) => s.category_id));
  const canAdd = activeCategories.some((c) => !usedCats.has(c.id));
  const [expandedSlots, setExpandedSlots] = useState<Set<number>>(new Set());

  const toggleExpand = (idx: number) => {
    setExpandedSlots((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const getCategoryItems = (categoryId: string) =>
    (menuItems || []).filter((mi) => mi.category_id === categoryId && mi.is_active);

  const toggleItemExclusion = (slotIdx: number, itemId: string) => {
    const slot = categorySlots[slotIdx];
    const excluded = slot.excluded_item_ids || [];
    const isExcluded = excluded.includes(itemId);
    updateSlot(slotIdx, {
      excluded_item_ids: isExcluded ? excluded.filter((id: string) => id !== itemId) : [...excluded, itemId],
    });
  };

  const toggleSelectAll = (slotIdx: number, items: MenuItemRow[]) => {
    const slot = categorySlots[slotIdx];
    const excluded = slot.excluded_item_ids || [];
    const allIncluded = items.every((mi) => !excluded.includes(mi.id));
    updateSlot(slotIdx, {
      excluded_item_ids: allIncluded ? items.map((mi) => mi.id) : [],
    });
  };

  return (
    <div className="p-6 space-y-4 min-h-[500px]">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Define which categories customers can choose from and how many items they can pick.</p>
        <Button type="button" variant="outline" size="sm" onClick={addSlot} disabled={!canAdd}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Category
        </Button>
      </div>

      {categorySlots.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <LayoutList className="h-10 w-10 text-muted-foreground/30 mb-3" />
          <p className="text-sm text-muted-foreground mb-1">No category slots defined</p>
          <p className="text-xs text-muted-foreground mb-4">Add categories that customers can pick items from.</p>
          <Button type="button" variant="outline" size="sm" onClick={addSlot} disabled={!canAdd}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Add First Category
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {categorySlots.map((slot: SlotEntry, idx: number) => {
            const catItems = getCategoryItems(slot.category_id);
            const excluded = slot.excluded_item_ids || [];
            const includedCount = catItems.length - excluded.length;
            const isExpanded = expandedSlots.has(idx);

            return (
              <Card key={idx} className="shadow-sm">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground font-medium w-6 text-center">{idx + 1}.</span>
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <Select value={slot.category_id} onValueChange={(v) => updateSlot(idx, { category_id: v, excluded_item_ids: [] })}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {activeCategories
                            .filter((c) => c.id === slot.category_id || !usedCats.has(c.id))
                            .map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-muted-foreground whitespace-nowrap">Select</Label>
                        <div className="flex items-center gap-1 bg-muted rounded-md px-1">
                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7"
                            onClick={() => updateSlot(idx, { quantity: Math.max(1, slot.quantity - 1) })}>
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="text-sm w-8 text-center tabular-nums font-medium">{slot.quantity}</span>
                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7"
                            onClick={() => updateSlot(idx, { quantity: slot.quantity + 1 })}>
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      <Input
                        placeholder="Custom label (optional)"
                        value={slot.label_override}
                        onChange={(e) => updateSlot(idx, { label_override: e.target.value })}
                        className="h-9 text-sm"
                      />
                    </div>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => removeSlot(idx)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Items toggle */}
                  {catItems.length > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={() => toggleExpand(idx)}
                        className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors ml-8"
                      >
                        {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                        {includedCount} of {catItems.length} items included
                      </button>

                      {isExpanded && (
                        <div className="ml-8 border rounded-md divide-y divide-border">
                          {/* Select All header */}
                          <label className="flex items-center gap-2 px-3 py-2 bg-muted/50 cursor-pointer hover:bg-muted">
                            <Checkbox
                              checked={excluded.length === 0}
                              onCheckedChange={() => toggleSelectAll(idx, catItems)}
                            />
                            <span className="text-sm font-medium">Select All ({catItems.length} items)</span>
                          </label>
                          {catItems.map((mi) => {
                            const isIncluded = !excluded.includes(mi.id);
                            return (
                              <label key={mi.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-muted/30">
                                <Checkbox
                                  checked={isIncluded}
                                  onCheckedChange={() => toggleItemExclusion(idx, mi.id)}
                                />
                                <span className={`text-sm ${isIncluded ? "text-foreground" : "text-muted-foreground line-through"}`}>
                                  {mi.name}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}

          <div className="flex items-center justify-between pt-2 px-2">
            <p className="text-sm text-muted-foreground">
              Total customer selections: <span className="font-semibold text-foreground">{totalSlotPicks}</span>
            </p>
            {canAdd && (
              <Button type="button" variant="ghost" size="sm" onClick={addSlot} className="text-xs">
                <Plus className="h-3 w-3 mr-1" /> Add Another
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PackageEditorPage;
